import React, { useState, useEffect } from 'react';
import axios from 'axios';
import logo from './assets/logo.svg';
import { API_BASE_URL } from './config';

const Login = ({ onLogin, onBack, branding }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // TOTP step
  const [totpStep, setTotpStep] = useState(false);
  const [partialToken, setPartialToken] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [totpLoading, setTotpLoading] = useState(false);

  // SSO
  const [ssoStatus, setSSOStatus] = useState(null); // { providers, enforce_sso, labels, oidc_login_url, saml_login_url }
  const [ldapStep, setLdapStep] = useState(false);
  const [ldapUsername, setLdapUsername] = useState('');
  const [ldapPassword, setLdapPassword] = useState('');
  const [ldapLoading, setLdapLoading] = useState(false);

  useEffect(() => {
    // Check for SSO token returned from OIDC/SAML callback
    const params = new URLSearchParams(window.location.search);
    const ssoToken = params.get('sso_token');
    const ssoError = params.get('sso_error');
    if (ssoToken) {
      window.history.replaceState({}, '', window.location.pathname);
      localStorage.setItem('token', ssoToken);
      onLogin(ssoToken);
      return;
    }
    if (ssoError) {
      window.history.replaceState({}, '', window.location.pathname);
      setError(`SSO error: ${ssoError.replace(/_/g, ' ')}`);
    }
    // Fetch SSO status (public endpoint)
    axios.get(`${API_BASE_URL}/auth/sso/status`).then(res => setSSOStatus(res.data)).catch(() => {});
  }, []);

  const inputSt = {
    width: '100%',
    padding: '0.75rem',
    borderRadius: '6px',
    border: '1px solid var(--border-color)',
    background: 'var(--bg-input)',
    color: 'var(--text-primary)',
    fontSize: '1rem',
    outline: 'none',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box',
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('username', username);
      formData.append('password', password);
      const response = await axios.post(`${API_BASE_URL}/token`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (response.data.totp_required) {
        setPartialToken(response.data.partial_token);
        setTotpStep(true);
        setTotpCode('');
      } else {
        localStorage.setItem('token', response.data.access_token);
        onLogin(response.data.access_token);
      }
    } catch (err) {
      setError('Invalid username or password');
    } finally {
      setLoading(false);
    }
  };

  const handleTotpSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setTotpLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/token/totp`, {
        partial_token: partialToken,
        code: totpCode.replace(/\s/g, ''),
      });
      localStorage.setItem('token', response.data.access_token);
      onLogin(response.data.access_token);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid code — please try again');
      setTotpCode('');
    } finally {
      setTotpLoading(false);
    }
  };

  const handleLdapSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLdapLoading(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/auth/ldap/token`, { username: ldapUsername, password: ldapPassword });
      localStorage.setItem('token', res.data.access_token);
      onLogin(res.data.access_token);
    } catch (err) {
      setError(err.response?.data?.detail || 'LDAP authentication failed');
    } finally {
      setLdapLoading(false);
    }
  };

  const cardSt = {
    background: 'var(--bg-secondary)',
    padding: '2.5rem',
    borderRadius: '12px',
    boxShadow: 'var(--shadow-strong)',
    width: '100%',
    maxWidth: '400px',
    border: '1px solid var(--border-color)',
    position: 'relative',
  };

  const errorBox = error ? (
    <div style={{
      background: 'rgba(220, 53, 69, 0.1)', color: 'var(--error-color)',
      padding: '0.75rem', borderRadius: '6px', marginBottom: '1.5rem',
      textAlign: 'center', fontSize: '0.9rem', border: '1px solid var(--error-color)',
    }}>{error}</div>
  ) : null;

  const ssoProviders = ssoStatus?.providers || [];
  const ldapEnabled = ssoProviders.includes('ldap');
  const oidcEnabled = ssoProviders.includes('oidc');
  const samlEnabled = ssoProviders.includes('saml');
  const enforceSSO = ssoStatus?.enforce_sso;
  const oidcLabel = ssoStatus?.labels?.oidc || 'Sign in with SSO';
  const samlLabel = ssoStatus?.labels?.saml || 'Sign in with SAML SSO';

  const brandName = branding?.app_name || 'xavvy';
  const brandLogo = branding?.logo_url || logo;

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: 'var(--bg-primary)', color: 'var(--text-primary)', fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif" }}>
      <div style={cardSt}>
        {onBack && !totpStep && (
          <button onClick={onBack} style={{ position: 'absolute', top: '1rem', left: '1rem', background: 'transparent', border: 'none', color: 'var(--text-tertiary)', cursor: 'pointer', fontSize: '1.2rem', padding: '5px' }} title="Back">←</button>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '2rem' }}>
          <img src={brandLogo} alt={`${brandName} Logo`} style={{ width: '64px', height: '64px', marginBottom: '1rem', objectFit: 'contain' }} />
          {totpStep ? (
            <>
              <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 600, color: 'var(--text-primary)' }}>Two-Factor Auth</h1>
              <p style={{ margin: '0.5rem 0 0', color: 'var(--text-tertiary)', fontSize: '0.9rem', textAlign: 'center' }}>Enter the 6-digit code from your authenticator app</p>
            </>
          ) : (
            <>
              <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 600, color: 'var(--text-primary)' }}>Welcome to {brandName}</h1>
              <p style={{ margin: '0.5rem 0 0', color: 'var(--text-tertiary)', fontSize: '0.9rem' }}>Sign in to continue</p>
            </>
          )}
        </div>

        {totpStep ? (
          <form onSubmit={handleTotpSubmit}>
            {errorBox}
            <div style={{ marginBottom: '2rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>Authenticator Code</label>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                placeholder="000000"
                autoFocus
                style={{ ...inputSt, textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.4em', fontFamily: 'monospace' }}
                onFocus={(e) => e.target.style.borderColor = 'var(--accent-color)'}
                onBlur={(e) => e.target.style.borderColor = 'var(--border-color)'}
              />
            </div>
            <button
              type="submit"
              disabled={totpLoading || totpCode.length !== 6}
              style={{ width: '100%', padding: '0.875rem', background: 'var(--accent-color)', color: 'var(--text-inverse)', border: 'none', borderRadius: '6px', cursor: totpLoading || totpCode.length !== 6 ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '1rem', opacity: totpLoading || totpCode.length !== 6 ? 0.7 : 1 }}
            >
              {totpLoading ? 'Verifying...' : 'Verify'}
            </button>
            <button
              type="button"
              onClick={() => { setTotpStep(false); setPartialToken(''); setError(''); }}
              style={{ width: '100%', marginTop: '10px', padding: '0.6rem', background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border-color)', borderRadius: '6px', cursor: 'pointer', fontSize: '0.9rem' }}
            >
              ← Back to login
            </button>
          </form>
        ) : ldapStep ? (
          /* LDAP login form */
          <form onSubmit={handleLdapSubmit}>
            {errorBox}
            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>LDAP Username</label>
              <input
                type="text"
                value={ldapUsername}
                onChange={(e) => setLdapUsername(e.target.value)}
                placeholder="username or sAMAccountName"
                autoFocus
                style={inputSt}
                onFocus={(e) => e.target.style.borderColor = 'var(--accent-color)'}
                onBlur={(e) => e.target.style.borderColor = 'var(--border-color)'}
              />
            </div>
            <div style={{ marginBottom: '2rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>Password</label>
              <input
                type="password"
                value={ldapPassword}
                onChange={(e) => setLdapPassword(e.target.value)}
                placeholder="Directory password"
                style={inputSt}
                onFocus={(e) => e.target.style.borderColor = 'var(--accent-color)'}
                onBlur={(e) => e.target.style.borderColor = 'var(--border-color)'}
              />
            </div>
            <button type="submit" disabled={ldapLoading} style={{ width: '100%', padding: '0.875rem', background: 'var(--accent-color)', color: 'var(--text-inverse)', border: 'none', borderRadius: '6px', cursor: ldapLoading ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '1rem', opacity: ldapLoading ? 0.7 : 1 }}>
              {ldapLoading ? 'Authenticating...' : 'Sign in with LDAP'}
            </button>
            <button type="button" onClick={() => { setLdapStep(false); setError(''); }} style={{ width: '100%', marginTop: '10px', padding: '0.6rem', background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border-color)', borderRadius: '6px', cursor: 'pointer', fontSize: '0.9rem' }}>
              ← Back to login
            </button>
          </form>
        ) : (
          <>
            {/* SSO buttons (shown above local form when configured) */}
            {(oidcEnabled || samlEnabled || ldapEnabled) && (
              <div style={{ marginBottom: '1.5rem' }}>
                {oidcEnabled && (
                  <a href={`${API_BASE_URL}${ssoStatus.oidc_login_url}`} style={{ display: 'block', width: '100%', padding: '0.75rem', marginBottom: '0.75rem', background: 'var(--button-bg)', color: 'var(--text-primary)', border: '1px solid var(--border-color)', borderRadius: '6px', cursor: 'pointer', fontWeight: '500', fontSize: '0.95rem', textAlign: 'center', textDecoration: 'none' }}>
                    {oidcLabel}
                  </a>
                )}
                {samlEnabled && (
                  <a href={`${API_BASE_URL}${ssoStatus.saml_login_url}`} style={{ display: 'block', width: '100%', padding: '0.75rem', marginBottom: '0.75rem', background: 'var(--button-bg)', color: 'var(--text-primary)', border: '1px solid var(--border-color)', borderRadius: '6px', cursor: 'pointer', fontWeight: '500', fontSize: '0.95rem', textAlign: 'center', textDecoration: 'none' }}>
                    {samlLabel}
                  </a>
                )}
                {ldapEnabled && (
                  <button type="button" onClick={() => { setLdapStep(true); setError(''); }} style={{ display: 'block', width: '100%', padding: '0.75rem', marginBottom: enforceSSO ? 0 : '0.75rem', background: 'var(--button-bg)', color: 'var(--text-primary)', border: '1px solid var(--border-color)', borderRadius: '6px', cursor: 'pointer', fontWeight: '500', fontSize: '0.95rem', textAlign: 'center' }}>
                    Sign in with LDAP / Active Directory
                  </button>
                )}
                {!enforceSSO && (oidcEnabled || samlEnabled || ldapEnabled) && (
                  <div style={{ display: 'flex', alignItems: 'center', margin: '0.75rem 0', color: 'var(--text-tertiary)', fontSize: '0.85rem' }}>
                    <div style={{ flex: 1, height: '1px', background: 'var(--border-color)' }} />
                    <span style={{ margin: '0 0.75rem' }}>or</span>
                    <div style={{ flex: 1, height: '1px', background: 'var(--border-color)' }} />
                  </div>
                )}
              </div>
            )}

            {/* Local login form — hidden when SSO is enforced */}
            {!enforceSSO && (
              <form onSubmit={handleSubmit}>
                {errorBox}
                <div style={{ marginBottom: '1.25rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>Username</label>
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Enter your username"
                    style={inputSt}
                    onFocus={(e) => e.target.style.borderColor = 'var(--accent-color)'}
                    onBlur={(e) => e.target.style.borderColor = 'var(--border-color)'}
                  />
                </div>
                <div style={{ marginBottom: '2rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', color: 'var(--text-secondary)' }}>Password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    style={inputSt}
                    onFocus={(e) => e.target.style.borderColor = 'var(--accent-color)'}
                    onBlur={(e) => e.target.style.borderColor = 'var(--border-color)'}
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  style={{ width: '100%', padding: '0.875rem', background: 'var(--accent-color)', color: 'var(--text-inverse)', border: 'none', borderRadius: '6px', cursor: loading ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '1rem', transition: 'opacity 0.2s', opacity: loading ? 0.7 : 1 }}
                  onMouseOver={(e) => !loading && (e.target.style.opacity = '0.9')}
                  onMouseOut={(e) => !loading && (e.target.style.opacity = '1')}
                >
                  {loading ? 'Signing in...' : 'Sign In'}
                </button>
              </form>
            )}

            {/* Enforce SSO message when no local form shown */}
            {enforceSSO && !oidcEnabled && !samlEnabled && !ldapEnabled && (
              <div style={{ textAlign: 'center', color: 'var(--text-tertiary)', fontSize: '0.9rem' }}>
                Local login is disabled. Contact your administrator.
              </div>
            )}
            {enforceSSO && (oidcEnabled || samlEnabled || ldapEnabled) && !ldapStep && (
              <div style={{ marginTop: '0.5rem' }}>
                {errorBox}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Login;
